/**
 * V2 Tokenomics API Client
 */
import axios from 'axios';
import { BACKEND_URL } from '@/lib/backendUrl';

const API_BASE = BACKEND_URL || '';

const v2Api = axios.create({
  baseURL: `${API_BASE}/api/blockchain/v2`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Dashboard
export const getProtocolDashboard = () => v2Api.get('/dashboard/protocol').then(r => r.data);
export const getHotelDashboard = (hotelId) => v2Api.get(`/dashboard/hotel/${hotelId}`).then(r => r.data);

// Halving
export const getHalvingStatus = () => v2Api.get('/halving/status').then(r => r.data);
export const getHalvingSchedule = () => v2Api.get('/halving/schedule').then(r => r.data);

// Treasury
export const getBtcTreasury = () => v2Api.get('/treasury/btc').then(r => r.data);
export const getProtocolPools = () => v2Api.get('/treasury/pools').then(r => r.data);
export const getHotelTreasury = (hotelId) => v2Api.get(`/treasury/hotel/${hotelId}`).then(r => r.data);
export const getTreasuryTransactions = (params) => v2Api.get('/treasury/transactions', { params }).then(r => r.data);
export const initBtcLock = (mpcAddress) => v2Api.post(`/treasury/btc/init-lock?mpc_wallet_address=${encodeURIComponent(mpcAddress)}`).then(r => r.data);
export const extendBtcLock = (reason, months) => v2Api.post(`/treasury/btc/extend-lock?reason=${encodeURIComponent(reason)}&months=${months}`).then(r => r.data);

// Registration
export const submitRegistration = (data) => v2Api.post('/registration/submit', data).then(r => r.data);
export const approveRegistration = (regId, approverId) => v2Api.post(`/registration/${regId}/approve?approver_id=${approverId}`).then(r => r.data);
export const rejectRegistration = (regId, reason, rejectorId) => v2Api.post(`/registration/${regId}/reject?reason=${encodeURIComponent(reason)}&rejector_id=${rejectorId}`).then(r => r.data);
export const getRegistration = (regId) => v2Api.get(`/registration/${regId}`).then(r => r.data);
export const listRegistrations = (status) => v2Api.get(`/registration/${status ? `?status=${status}` : ''}`).then(r => r.data);
export const getMintPhases = (hotelId) => v2Api.get(`/registration/mint-phases/${hotelId}`).then(r => r.data);
export const getPhaseSchedule = () => v2Api.get('/registration/schedule/phases').then(r => r.data);

// Marina Escrow
export const getMarinaEscrowSummary = (hotelId) => v2Api.get(`/marina-escrow/summary/${hotelId}`).then(r => r.data);
export const getMarinaStaff = (hotelId) => v2Api.get(`/marina-escrow/staff/${hotelId}`).then(r => r.data);
export const registerMarinaStaff = (data) => v2Api.post('/marina-escrow/staff/register', data).then(r => r.data);
export const getPendingPayroll = (hotelId) => v2Api.get(`/marina-escrow/payroll/pending/${hotelId}`).then(r => r.data);
export const processPayroll = (hotelId) => v2Api.post(`/marina-escrow/payroll/process/${hotelId}`).then(r => r.data);
export const getPayrollHistory = (hotelId) => v2Api.get(`/marina-escrow/payroll/history/${hotelId}`).then(r => r.data);
export const allocateTaskPayment = (params) => v2Api.post('/marina-escrow/allocate-task', null, { params }).then(r => r.data);
export const allocateTip = (data) => v2Api.post('/marina-escrow/tips/allocate', data).then(r => r.data);
export const getStaffPayments = (hotelId, staffId) => v2Api.get(`/marina-escrow/payments/${hotelId}/${staffId}`).then(r => r.data);

// Staking
export const getStakeStatus = (hotelId) => v2Api.get(`/staking/hotel/${hotelId}`).then(r => r.data);
export const evaluateStaking = (hotelId, score) => v2Api.post(`/staking/evaluate/${hotelId}?score=${score}`).then(r => r.data);
export const distributeReward = (hotelId, amount) => v2Api.post(`/staking/distribute-reward/${hotelId}?amount=${amount}`).then(r => r.data);
export const getSlashHistory = (hotelId) => v2Api.get(`/staking/slash-history/${hotelId}`).then(r => r.data);
export const getRewardHistory = (hotelId) => v2Api.get(`/staking/reward-history/${hotelId}`).then(r => r.data);

// Whistleblower
export const fileClaim = (data) => v2Api.post('/whistleblower/claims', data).then(r => r.data);
export const fileDefense = (claimId, data) => v2Api.post(`/whistleblower/claims/${claimId}/defense`, data).then(r => r.data);
export const submitVerdict = (claimId, params) => v2Api.post(`/whistleblower/claims/${claimId}/verdict`, null, { params }).then(r => r.data);
export const getClaims = (params) => v2Api.get('/whistleblower/claims', { params }).then(r => r.data);
export const getClaimDetail = (claimId) => v2Api.get(`/whistleblower/claims/${claimId}`).then(r => r.data);

// Linen Lifecycle
export const registerLinen = (data) => v2Api.post('/linen/register', data).then(r => r.data);
export const recordWash = (data) => v2Api.post('/linen/wash', data).then(r => r.data);
export const assignLinen = (data) => v2Api.post('/linen/assign', data).then(r => r.data);
export const retireLinen = (data) => v2Api.post('/linen/retire', data).then(r => r.data);
export const getLinenInventory = (hotelId, status) => v2Api.get(`/linen/inventory/${hotelId}`, { params: status ? { status } : {} }).then(r => r.data);
export const getLinenItem = (hotelId, rfidTag) => v2Api.get(`/linen/item/${hotelId}/${rfidTag}`).then(r => r.data);
export const getLinenHistory = (hotelId, rfidTag) => v2Api.get(`/linen/history/${hotelId}/${rfidTag}`).then(r => r.data);
export const computeEcoScore = (hotelId) => v2Api.post(`/linen/eco-score/compute/${hotelId}`).then(r => r.data);
export const getEcoScore = (hotelId) => v2Api.get(`/linen/eco-score/${hotelId}`).then(r => r.data);

// Veteran Linen
export const flagForInspection = (data) => v2Api.post('/linen/veteran/flag', data).then(r => r.data);
export const getInspectionQueue = (hotelId) => v2Api.get(`/linen/veteran/inspection-queue/${hotelId}`).then(r => r.data);
export const inspectLinen = (data) => v2Api.post('/linen/veteran/inspect', data).then(r => r.data);
export const getVeteranInventory = (hotelId) => v2Api.get(`/linen/veteran/inventory/${hotelId}`).then(r => r.data);
export const configureVeteranRewards = (data) => v2Api.post('/linen/veteran/config/rewards', data).then(r => r.data);
export const getVeteranRewardConfig = (hotelId) => v2Api.get(`/linen/veteran/config/rewards/${hotelId}`).then(r => r.data);
export const guestVeteranOptIn = (data) => v2Api.post('/linen/veteran/guest/optin', data).then(r => r.data);
export const getGuestEcoRewards = (guestId) => v2Api.get(`/linen/veteran/guest/rewards/${guestId}`).then(r => r.data);
export const getVeteranStats = (hotelId) => v2Api.get(`/linen/veteran/stats/${hotelId}`).then(r => r.data);
